
import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneIcon } from "lucide-react";
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useTranslation } from 'react-i18next';
import { useDirection } from '@/providers/DirectionProvider';

interface PhoneVerificationProps {
  phone: string;
  onVerified: () => void;
  isPostLogin?: boolean;
  autoSend?: boolean;
}

const PhoneVerification: React.FC<PhoneVerificationProps> = ({
  phone,
  onVerified,
  isPostLogin = false,
  autoSend = false
}) => {
  const [phoneNumber, setPhoneNumber] = useState(phone.replace(/\D/g, '').slice(0, 10));
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerificationInput, setShowVerificationInput] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const { setPhoneVerified, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { direction } = useDirection();
  const hasAutoSent = useRef(false);

  // Auto-send OTP when component mounts if autoSend is true and phone is provided
  useEffect(() => {
    if (autoSend && phone && !hasAutoSent.current) {
      hasAutoSent.current = true;
      sendOtpCode(phone);
    }
  }, [autoSend, phone]);

  const validateIsraeliPhone = (phone: string): boolean => {
    const phoneRegex = /^05\d{8}$/;
    return phoneRegex.test(phone);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only digits, remove everything else
    const value = e.target.value.replace(/\D/g, '');
    // Limit to 10 digits (Israeli mobile format)
    setPhoneNumber(value.slice(0, 10));
  };

  const normalizePhone = (phone: string): string => {
    return phone.replace(/[-\s]/g, '');
  };

  const checkPhoneExists = async (phone: string): Promise<boolean> => {
    const normalizedPhone = normalizePhone(phone);
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id')
      .eq('phone', normalizedPhone)
      .neq('id', user?.id || '')
      .maybeSingle();

    if (error) {
      console.error('Error checking phone existence:', error);
      return false;
    }

    return data !== null;
  };

  const sendOtpCode = async (phoneToSend: string) => {
    if (!phoneToSend) {
      toast({
        title: t('auth.missingFields'),
        description: t('loginForm.phonePlaceholder'),
        variant: "destructive",
      });
      return;
    }

    // Validate Israeli phone format
    if (!validateIsraeliPhone(phoneToSend)) {
      toast({
        title: t('registerFormHook.invalidPhone'),
        description: t('registerFormHook.invalidPhoneDescription'),
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);

    try {
      // Check if phone already exists (excluding current user)
      const phoneExists = await checkPhoneExists(phoneToSend);
      if (phoneExists) {
        toast({
          title: t('registerFormHook.phoneExists'),
          description: t('registerFormHook.phoneExistsDescription'),
          variant: "destructive",
        });
        setIsSending(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('send-otp-consumer', {
        body: { phone: phoneToSend }
      });

      if (error) {
        throw error;
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      toast({
        title: t('auth.verificationCodeSent'),
        description: t('auth.verificationCodeSent'),
      });

      setShowVerificationInput(true);

    } catch (error) {
      console.error('Error sending verification code:', error);
      toast({
        title: t('common.error'),
        description: (error as Error).message || t('common.tryAgain'),
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendOtpCode(phoneNumber);
  };

  const handleResendCode = () => {
    hasAutoSent.current = false;
    setShowVerificationInput(false);
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!verificationCode) {
      toast({
        title: t('auth.missingFields'),
        description: t('auth.enterVerificationCode'),
        variant: "destructive",
      });
      return;
    }
    
    setIsVerifying(true);
    
    try {
      // Call the verify-otp-consumer Edge Function
      const { data, error } = await supabase.functions.invoke('verify-otp-consumer', {
        body: {
          phone: phoneNumber,
          otp: verificationCode,
          userId: user?.id
        }
      });
      
      if (error) {
        throw error;
      }
      
      if (!data?.verified) {
        throw new Error(data?.error || 'קוד לא תקין');
      }

      // Clean up localStorage
      localStorage.removeItem('pendingPhoneVerification');

      // Push event to dataLayer for analytics - only for initial registration
      if (isPostLogin && typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          'event': 'customer_register'
        });
        console.log('[PhoneVerification] dataLayer event pushed: customer_register');
      }

      // Phone verified - user_profiles.phone_verified is updated by verify-otp-consumer
      // No need to update auth.users metadata (user_profiles is source of truth)

      toast({
        title: t('auth.verify'),
        description: t('auth.loginSuccess'),
      });
      
      setPhoneVerified(true);
      
      // Always call onVerified - let the parent component decide what to do
      onVerified();
    } catch (error) {
      console.error('Error verifying code:', error);
      toast({
        title: t('common.error'),
        description: (error as Error).message || t('common.tryAgain'),
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="space-y-4" dir={direction}>
      <Alert variant="warning">
        <AlertDescription>
          {t('auth.phoneVerificationDescription')}
        </AlertDescription>
      </Alert>
      
      {!showVerificationInput ? (
        <form onSubmit={handleSendCode} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="verification-phone">{t('loginForm.phoneNumber')}</Label>
            <div className="relative">
              <PhoneIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <Input
                id="verification-phone"
                placeholder="0541234567"
                value={phoneNumber}
                onChange={handlePhoneChange}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={10}
                dir="ltr"
                className="text-left pr-10"
              />
            </div>
            {phoneNumber && !validateIsraeliPhone(phoneNumber) && (
              <p className="text-sm text-destructive mt-1">
                {t('auth.invalidPhoneFormat')}
              </p>
            )}
          </div>
          
          <Button
            type="submit"
            className="w-full bg-teal-500 hover:bg-teal-600 text-white"
            disabled={isSending}
          >
            {isSending ? t('common.loading') : t('loginForm.sendVerificationCode')}
          </Button>
        </form>
      ) : (
        <form onSubmit={handleVerifyCode} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="verification-code">{t('auth.enterVerificationCode')}</Label>
            <Input
              id="verification-code"
              placeholder={t('auth.enterVerificationCode')}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              dir="ltr"
              className="text-center"
            />
          </div>
          
          <Button
            type="submit"
            className="w-full bg-teal-500 hover:bg-teal-600 text-white"
            disabled={isVerifying}
          >
            {isVerifying ? t('common.loading') : t('auth.verify')}
          </Button>
          
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleResendCode}
          >
            {t('auth.resendCode')}
          </Button>
        </form>
      )}
    </div>
  );
};

export default PhoneVerification;
